import { io } from 'socket.io-client';
// Import estático é seguro: api.js não importa este módulo, então não há ciclo.
// Melhor que import() dinâmico aqui — não dá pra depender de baixar um chunk
// justamente no momento em que a conexão está falhando.
import api from './api';

const SOCKET_URL = (process.env.NODE_ENV === 'production' || window.location.protocol === 'https:')
  ? window.location.origin
  : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace('/api', '');

let socket = null;
let _tenantId = null;

function getToken() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}').token || localStorage.getItem('token') || null;
  } catch {
    return null;
  }
}

// Renova o JWT quando o socket é recusado por autenticação. Sem isto, uma aba
// aberta a noite inteira acorda com token vencido e nunca mais recebe evento —
// o refresh do axios só dispara quando há chamada de API, e ficar olhando a tela
// não gera nenhuma.
let renovandoToken = false;
let ultimaRenovacao = 0;
async function tentarRenovarToken() {
  // Trava simples: o socket.io retenta a cada 1-5s e dispararia uma enxurrada
  // de refresh. Um a cada 30s é suficiente pra recuperar rápido sem martelar.
  const agora = Date.now();
  if (renovandoToken || agora - ultimaRenovacao < 30000) return;
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return; // sem refresh não há o que fazer; o axios leva pro login
  renovandoToken = true;
  ultimaRenovacao = agora;
  try {
    const { data } = await api.post('/auth/refresh-token', { refreshToken });
    if (data?.token) {
      localStorage.setItem('token', data.token);
      if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
      // auth é função (ver initSocket), então a próxima tentativa já pega o novo.
      if (socket) { try { socket.disconnect(); socket.connect(); } catch (e) {} }
    }
  } catch (e) {
    // Refresh inválido/expirado: o interceptor do axios cuida do logout.
  } finally {
    renovandoToken = false;
  }
}

function initSocket() {
  socket = io(SOCKET_URL, {
    // Why-incidente 2026-09-28: isto era `auth: { token }`, com o token lido UMA
    // vez na criação do socket. O socket.io reusa esse mesmo objeto em toda
    // reconexão — então, quando o JWT expirava (JWT_EXPIRES_IN=24h), todas as
    // tentativas de reconectar passavam a mandar o token morto e o servidor
    // respondia `auth:invalid_token`. Sem log no servidor e só um console.error
    // no navegador, ninguém via: o painel seguia "funcionando" pelo refetch de
    // 45s, então o sintoma virou "mensagem demora a aparecer" em vez de "tempo
    // real caiu". Ficou 3 dias assim (25/09 19:27 → 28/09), com ZERO conexões.
    //
    // Na forma de função, o socket.io chama isto a CADA tentativa de conexão,
    // então a reconexão sempre leva o token atual do localStorage.
    auth: (cb) => cb({ token: getToken() }),
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: Infinity,
    timeout: 20000,
    // WebSocket PRIMEIRO (2026-09-10). Com 'polling' na frente, o cliente abria
    // em long-polling e na prática quase nunca subia pra websocket: o log do
    // nginx mostrou 345 requisições em polling contra 6 em websocket, mais 22
    // respostas 400 (sessão de polling expirada). Consequências sentidas pelos
    // atendentes: mensagem demorando pra aparecer e, quando a sessão morria
    // entre dois polls, evento perdido — a mensagem só surgia ao recarregar
    // (foi o caso do 3º PDF que "sumiu" do grupo).
    // O servidor aceita upgrade normalmente (testado: HTTP 101), então abrir
    // direto em websocket é seguro; 'polling' fica só como plano B pra rede
    // que bloqueia websocket.
    transports: ['websocket', 'polling'],
    upgrade: true,
    rememberUpgrade: true
  });

  socket.on('connect', () => {
    console.log('[Socket] Conectado:', socket.id);
    if (_tenantId) {
      socket.emit('join:tenant', _tenantId);
      console.log('[Socket] Entrou na sala tenant_' + _tenantId);
    }
  });

  socket.on('connect_error', (err) => {
    console.error('[Socket] Erro de conexão:', err.message);
    // Token expirado com a aba parada: nenhuma chamada de API aconteceu pra
    // renovar via interceptor do axios, então o auth dinâmico sozinho continuaria
    // mandando um token vencido. Aqui renovamos e tentamos de novo.
    if (String(err.message || '').startsWith('auth:')) tentarRenovarToken();
  });

  socket.on('reconnect', () => {
    // Reenvia token atualizado ao reconectar
    if (_tenantId) socket.emit('join:tenant', _tenantId);
  });

  socket.on('disconnect', (reason) => {
    console.log('[Socket] Desconectado:', reason);
  });

  return socket;
}

function ensureConnected() {
  if (!socket) { initSocket(); return; }
  if (!socket.connected) {
    try { socket.connect(); } catch (e) {}
  } else if (_tenantId) {
    socket.emit('join:tenant', _tenantId);
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') ensureConnected();
  });
  window.addEventListener('focus', ensureConnected);
  window.addEventListener('pageshow', ensureConnected);
  window.addEventListener('online', ensureConnected);

  // Heartbeat: em navegadores móveis o socket.io pode “achar” que está conectado
  // mas o transporte já foi congelado pelo SO. Verificamos periodicamente.
  setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    if (!socket) return;
    if (!socket.connected) ensureConnected();
  }, 15000);
}

const socketService = {
  connect(tenantId) {
    _tenantId = tenantId;
    if (socket && socket.connected) {
      if (tenantId) socket.emit('join:tenant', tenantId);
      return socket;
    }
    if (socket) socket.disconnect();
    return initSocket();
  },

  disconnect() {
    if (socket) { socket.disconnect(); socket = null; }
  },

  on(eventName, callback) {
    if (!socket) initSocket();
    socket.on(eventName, callback);
  },

  off(eventName, callback) {
    if (!socket) return;
    if (callback) socket.off(eventName, callback);
    else socket.off(eventName);
  },

  emit(eventName, payload) {
    if (socket && socket.connected) socket.emit(eventName, payload);
  },

  // Inicializa socket se necessário e entra na sala do tenant
  joinTenant(tenantId) {
    _tenantId = tenantId;
    if (!socket || !socket.connected) {
      initSocket();
      return;
    }
    socket.emit('join:tenant', tenantId);
  },

  onMessageNew(callback) {
    this.on('message:new', callback);
  },
  onWhatsAppStatus(callback) {
    this.on('whatsapp:status', callback);
  },
  onWhatsAppQR(callback) {
    this.on('whatsapp:qr', callback);
  }
};

export default socketService;